import { Role } from 'Components/Context/RBACContext';
import { Status, UserResponse } from 'Models/User';

/**
 * Determines how User names are formatted.
 */
export enum UserNameFormat {
    FIRST_SPACE_LAST,
    LAST_COMMA_FIRST,
}

/**
 * Return the User's department when the User is provided as a subject within a list of Users.
 */
export const getDepartmentFromSubject = (subject: string, users: UserResponse[]): string | undefined => {
    const user = users.find((user) => user.cognito_subject === subject);
    if (user) {
        return user.department;
    } else {
        return undefined;
    }
};

/**
 * Return the User's name in a standard format when the User is provided as an object.
 * @param format - Use UserNameFormat.FIRST_SPACE_LAST when showing a single user. Otherwise, use the default (UserNameFormat.LAST_COMMA_FIRST) when multiple users are displayed to make sorting possible.
 */
export const getUserNameFromUser = (user: UserResponse, format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): string => {
    return applyUserNameFormatting(user, format);
};

/**
 * Return the User's name in a standard format when the User is provided as a subject within a list of Users.
 * @param format - Use UserNameFormat.FIRST_SPACE_LAST when showing a single user. Otherwise, use the default (UserNameFormat.LAST_COMMA_FIRST) when multiple users are displayed to make sorting possible.
 */
export const getUserNameFromSubject = (subject: string, users: UserResponse[], format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): string | undefined => {
    const user = users.find((user) => user.cognito_subject === subject);
    if (user) {
        return applyUserNameFormatting(user, format);
    } else {
        return undefined;
    }
};

/**
 * Standardize how Users are displayed throughout the application.
 * @param format - Use UserNameFormat.FIRST_SPACE_LAST when showing a single user. Otherwise, use the default (UserNameFormat.LAST_COMMA_FIRST) when multiple users are displayed to make sorting possible.
 */
const applyUserNameFormatting = (user: UserResponse, format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): string => {
    let name = '';
    if (format === UserNameFormat.LAST_COMMA_FIRST) {
        name = `${user.last_name}, ${user.first_name}`;
    } else {
        name = `${user.first_name} ${user.last_name}`;
    }

    if (user.status === Status.INACTIVE) {
        return `${name} (Inactive)`;
    } else {
        return name;
    }
};

/**
 * Given two User subjects, compare the names of their corresponding User objects for sorting.
 */
export const compareUsersBySubjectForSorting = (subject1: string, subject2: string, users: UserResponse[], format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): number => {
    const user_name_a = getUserNameFromSubject(subject1, users, format);
    const user_name_b = getUserNameFromSubject(subject2, users, format);

    if (user_name_a && user_name_b) {
        return user_name_a.localeCompare(user_name_b);
    }

    if (user_name_a && !user_name_b) {
        return -1;
    }

    if (!user_name_a && user_name_b) {
        return 1;
    }

    return 0;
};

/**
 * Standardizes sorting for UserResponse objects. The array of users is sorted in place and then returned.
 * By default, assumes the resulting users will be displayed in the "Last, First" format.
 */
export const sortFormattedUsers = (users: UserResponse[], format: UserNameFormat = UserNameFormat.LAST_COMMA_FIRST): UserResponse[] => {
    return users.sort((userA, userB) => {
        return compareUsersBySubjectForSorting(userA.cognito_subject, userB.cognito_subject, users, format);
    });
};

// TODO: Centralize other sets of roles here, and possibly move them to a more appropriate file.

/**
 * The roles a user must have in order to access the Risk Management module.
 */
export const RISK_ROLES = [Role.ADMIN, Role.RISK_MANAGER, Role.RISK_USER];

/**
 * The roles a user must have in order to have full access to the Third-Party Risk Management module.
 * Any users can manage their own drafts of new third parties and third party services. All other TPRM operations are restricted to users with these roles.
 */
export const TPRM_ROLES = [Role.ADMIN, Role.THIRD_PARTY_USER];

export const userHasFullAccessToTprm = (user: UserResponse): boolean => {
    return user.roles.some((role) => TPRM_ROLES.includes(role));
};

// TODO: This could be improved if the logged-in user were available in the app-wide context, and could be accessed here via context, rather than passing in roles.
export const loggedInUserHasFullAccessToTprm = (loggedInUserRoles: Role[]): boolean => {
    return loggedInUserRoles.some((role) => TPRM_ROLES.includes(role));
};
